<template>
  <Banner
    :default-background1="true"
    icon="person_outline"
    icon-size="36"
    data-testid="application-view-header-banner"
  >
    <template #title>
      <v-row>
        <h1 class="appTitle">
          {{ getValue('student.familyName') }},
          {{ getValue('student.firstName') }}
          <Chip
            v-if="showStatusChip"
            v-bind="getStatusChipDetails(getValue('applicationStatus'))"
            :class="statusBasedClass(getValue('applicationStatus'))"
            data-testid="application-view-header-application-status"
          />
        </h1>
      </v-row>
    </template>
    <template #subtitle>
      <v-row :class="isY67TApplication ? 'pt-2 ml-n10' : 'pt-2 ml-n8'">
        <template v-if="!isY67TApplication">
          <v-col
            class="pl-0 pt-0 text-no-wrap"
            data-testid="application-view-header-application-id"
          >
            <template v-if="isShsOffer">
              <strong>SHS Application ID:</strong><br />{{
                getValue('shsocId')
              }}
            </template>
            <template v-else>
              <strong>Application ID:</strong><br />{{ appID }}
            </template>
          </v-col>
        </template>
        <template v-if="$store.state.applicationType === 'LA'">
          <!-- create application type v col -->
          <v-col
            class="pt-0 text-no-wrap"
            data-testid="application-view-header-application-type"
          >
            <strong>Application type:</strong><br />
            {{ getEnrolmentType(application) }}
          </v-col>
        </template>
        <template v-if="isShsOffer">
          <v-col class="pt-0 text-no-wrap" data-testid="scholastic-year">
            <strong>Scholastic year:</strong> <br />{{
              getFormattedScholasticYear(getValue('scholasticYear'))
            }}
          </v-col>
          <v-col class="pt-0" data-testid="shs-offer-date">
            <strong>Accepted on:</strong><br />
            {{ displayDate(getValue('issuedDate')) }}
          </v-col>
        </template>

        <v-col
          v-if="isY67TApplication && isSecondarySchool"
          class="pt-0"
          data-testid="application-view-primary-srn-details"
        >
          {{ getValue('currentPrimarySchool.schoolName') }}<br />
          <strong>SRN:</strong> {{ getValue('student.SRN') }}
        </v-col>
        <v-col
          v-if="isOutOfAreaApplication || isY67TApplication"
          class="pt-0"
          data-testid="application-view-header-address"
        >
          <strong>Home address:</strong><br />
          {{ getValue('residentialAddress.addressLine1') }}<br />
          {{ getValue('residentialAddress.suburbName') }}
          {{ getValue('residentialAddress.stateCode') }}
          {{ getValue('residentialAddress.postCode') }}
        </v-col>
        <v-col
          v-if="isOutOfAreaApplication && isOoaLocalSchool"
          class="pt-0"
          data-testid="application-view-header-local-school"
        >
          Local school{{ application.ooaLocalSchool.length > 1 ? 's' : '' }}:
          <span
            v-for="schoolName in application.ooaLocalSchool"
            :key="schoolName"
            class="d-block"
          >
            {{ schoolName }}
          </span>
        </v-col>
        <v-col
          class="pt-0"
          data-testid="application-view-header-contact-details"
        >
          <strong>Contact details:</strong><br />
          <span class="text-no-wrap">
            {{ parentCarerTitle }}
            {{ parentCarerGivenName }}
            {{ parentCarerFamilyName }}
            <a
              v-if="contactDetails('EMAIL')"
              :href="`mailto:${contactDetails('EMAIL')}`"
              class="white--text"
            >
              ({{ contactDetails('EMAIL') }})
            </a>
          </span>
          <br />{{ contactDetails('PHMOB') }}
        </v-col>
        <!-- isSupportClassApplication function used to determine whether to display -->
        <!-- Enrolment Details for Support Class Enrolments -->
        <!-- Note: enrolmentTypeName and SubEnrolmentTypeName may be null -->
        <!-- for older support class applications but still dispaly the headers -->
        <v-col
          v-if="isSupportClassApplication"
          class="pl-0 pt-0 text-no-wrap"
          data-testid="application-view-header-sce-details"
        >
          <strong>Enrolment details:</strong><br />
          <span
            class="text-no-wrap"
            v-if="application.enrolmentTypeName && subEnrolmentTypeName"
          >
            {{ application.enrolmentTypeName || '' }}
            {{ application.subEnrolmentTypeName || '' }}
          </span>
          <br />{{ contactDetails('PHMOB') }}
        </v-col>
        <v-col
          v-if="isOutOfAreaApplication"
          class="pt-0"
          data-testid="application-view-header-siblings"
        >
          <strong>Siblings attending:</strong><br />{{
            getValue('noOfSiblings')
          }}
        </v-col>
        <v-col v-if="isY67TApplication && isSecondarySchool" class="pt-0">
          <strong>School preference:</strong><br />{{ preferenceNo }}
        </v-col>
      </v-row>
    </template>
    <template #rightPannel>
      <div v-if="isArchived" class="pagination">
        <ArchiveRecordPagination />
      </div>
      <div v-else class="pagination"><RecordPagination /></div>
    </template>
  </Banner>
</template>

<script>
import { Banner, Chip } from '@nswdoe/doe-ui-core'
import fieldHelperMixin from '@/helpers/fieldHelperMixin'
import RecordPagination from '@/components/application/RecordPagination.vue'
import ArchiveRecordPagination from '@/components/application/ArchiveRecordPagination.vue'
import { numToOrdinal } from '@/helpers/generalUtils'
import formatters from '@/helpers/formatters'
import { mapGetters } from 'vuex'
import { getEnrolmentType } from '@/helpers/applicationDataHelper.js'

import {
  STATUS_CHIPS,
  STATUS_CHIPS_DARK_BACKGROUND,
  OOA_STATUS_CHIPS_DETAILS_PAGE,
  OOA_STATUS_CHIPS_DARK_BACKGROUND,
  Y67T_STATUS_CHIPS,
  Y67T_STATUS_CHIPS_DARK_BACKGROUND,
  STREAM,
  REF_DATA,
  OOA_OFFERED_STATUSES,
  STATUS
} from '@/constants'

const { displayDate } = formatters

export default {
  name: 'ApplicationViewHeader',
  components: { Banner, Chip, RecordPagination, ArchiveRecordPagination },
  data() {
    return {
      displayDate,
      getEnrolmentType,
      STREAM
    }
  },
  props: {
    isLocalAreaApplication: {
      type: Boolean,
      default: false
    },
    isOutOfAreaApplication: {
      type: Boolean,
      default: false
    },
    isY67TApplication: {
      type: Boolean,
      default: false
    },
    isShsOffer: {
      type: Boolean,
      default: false
    }
  },
  mixins: [fieldHelperMixin],
  computed: {
    ...mapGetters([
      'selectedSchool',
      'application',
      'currentStream',
      'referenceData'
    ]),
    isOoaLocalSchool() {
      return this.application?.ooaLocalSchool?.length > 0
    },
    contact() {
      if (this.isLocalAreaApplication) {
        const isCompletingApplication = this.getValue('parentCarers').find(
          (parent) => parent.isCompletingApplication === true
        )
        if (isCompletingApplication) {
          return isCompletingApplication
        } else {
          return this.getValue('parentCarers').find(
            (parent) => parent.isEnrolmentOwner === true
          )
        }
      }

      return this.getValue('parentCarers').find(
        (parent) => parent.parentCarerCompletingApp === true
      )
    },
    preferenceNo() {
      if (this.selectedSecondarySchoolData?.preferenceNo) {
        return numToOrdinal(this.selectedSecondarySchoolData.preferenceNo)
      }
      return ''
    },
    isSecondarySchool() {
      if (this.currentStream === STREAM.Y67T_SECONDARY) {
        return true
      } else if (
        this.currentStream === STREAM.ARCHIVE &&
        !this.$route.params.primary
      ) {
        return true
      }
      return false
    },

    isSupportClassApplication() {
      return this.application.enrolmentType === 'CORE_SCE'
    },
    enrolmentTypeName() {
      return this.application.enrolmentTypeName
        ? this.application.enrolmentTypeName
        : ''
    },
    subEnrolmentTypeName() {
      return this.application.subEnrolmentTypeName
        ? this.application.subEnrolmentTypeName
        : ''
    },
    parentCarerTitle() {
      return this.contact?.parentCarerTitle
    },
    parentCarerGivenName() {
      return this.contact?.parentCarerGivenName
    },
    parentCarerFamilyName() {
      return this.contact?.parentCarerFamilyName
    },
    applicationStatus() {
      return this.application.applicationStatus
    },
    showStatusChip() {
      return (
        !this.isShsOffer && this.getStatusChipDetails(this.applicationStatus)
      )
    },
    isArchived() {
      return this.currentStream === STREAM.ARCHIVE
    },
    appID() {
      let applicationID = this.application?.applicationID || ''
      const isIssuedApp =
        applicationID.includes('MEO-') &&
        this.application?.applicationStatus === STATUS.ISSUED

      if (isIssuedApp) {
        // This is to ensure consistency with the EHUB dashboard
        if (this.application?.isInArea) {
          return applicationID.replace('MEO-', '')
        }

        if (this.application?.isInArea === false) {
          return applicationID.replace('MEO', 'OOA')
        }
      }
      return this.getValue('applicationID')
    }
  },
  methods: {
    statusBasedClass(status) {
      const baseClass = 'application-status'

      if (
        this.isOutOfAreaApplication &&
        OOA_OFFERED_STATUSES.includes(status)
      ) {
        return `${baseClass} ooa-offered`
      }

      if (this.isY67TApplication) {
        return `${baseClass} y67t-${status.toLowerCase()}`
      }

      return baseClass
    },
    getStatusChipDetails(status) {
      if (this.isLocalAreaApplication) {
        return STATUS_CHIPS_DARK_BACKGROUND[status]
          ? STATUS_CHIPS_DARK_BACKGROUND[status]
          : STATUS_CHIPS[status]
      }
      if (this.isOutOfAreaApplication) {
        return (
          OOA_STATUS_CHIPS_DARK_BACKGROUND[status] ||
          OOA_STATUS_CHIPS_DETAILS_PAGE[status]
        )
      }

      return Y67T_STATUS_CHIPS_DARK_BACKGROUND[status]
        ? Y67T_STATUS_CHIPS_DARK_BACKGROUND[status]
        : Y67T_STATUS_CHIPS[status]
    },
    contactDetails(type) {
      if (!this.isLocalAreaApplication && type === 'EMAIL') {
        return this.contact?.parentCarerEmail
      }
      const contact = this.contact?.contactDetails?.find(
        (contact) => contact.contactType === type
      )

      return contact?.contactValue || ''
    },
    getFormattedScholasticYear(scholasticYear) {
      return this.referenceData(REF_DATA.SCHOLASTIC_YEAR).find(
        (sy) => sy.value === scholasticYear
      )?.text
    }
  },
  created() {
    const ooaHighSchools = this.application.ooaHighSchools || []
    const localHighSchool = this.application.localHighSchool || {}
    this.selectedSecondarySchoolData = [
      ...ooaHighSchools,
      localHighSchool
    ].find((school) => school.schoolCode === this.selectedSchool.schoolCode)
  }
}
</script>

<style scoped lang="scss">
::v-deep .float-left {
  margin-left: 24px;
}
::v-deep .application-status {
  min-width: 142px;
  .v-chip__content span,
  .v-chip__content {
    width: 100%;
    text-align: center;
    font-weight: 700;
  }
}

::v-deep .v-chip.v-chip--outlined.v-chip.v-chip.y67t-offered,
::v-deep .v-chip.v-chip--outlined.v-chip.v-chip.ooa-offered {
  background-color: $ads-primary-blue !important;
}

.appTitle {
  font-size: 34px;
  margin-left: 19px;
  padding-top: 3px;
}

@include desktop {
  .pagination {
    flex: 1;
    margin: 4rem 6rem 0 0;
  }
}
</style>
